import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GitBranch, Loader } from 'lucide-react';
import { Modal } from '../ui/Modal';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import { connectableAccounts, vaultSignInOptions } from '../GitConnectModal';
import { accountsForTeamRepo, joinOptions, teamRepoLabel } from '../../services/git/teamRepo';
import { hostOf } from '../../graph/repoRefs';
import { useVaultStore } from '../../store/vaultStore';

/** Vault items that can sign in to git: Password (HTTPS token) and SFTP (SSH key). Names only. */
const GIT_CAPABLE = new Set(['password', 'sftp']);
const VAULT = 'vault';

/**
 * Join the repository stored on a team (card 84628c27) with YOUR sign-in:
 * a signed-in account on the repository's server, or a Vault item. The
 * repository is fixed — it is the team's; only an owner/admin moves it.
 * Opened when Join cannot go in one click (no account, or several).
 */
export default function GitJoinTeamModal({ isOpen, onClose, scope, onJoin, loading = false }) {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState(null);
  const [choice, setChoice] = useState('');
  const [vaultItemId, setVaultItemId] = useState('');
  const [error, setError] = useState('');
  const vaultItems = useVaultStore((s) => s.items);
  const vaultUnlocked = useVaultStore((s) => Boolean(s.status?.unlocked));
  const wasOpenRef = useRef(false);
  const teamRepo = scope?.teamRepo;

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }
    if (wasOpenRef.current) return;
    wasOpenRef.current = true;
    setAccounts(null);
    setError('');
    setVaultItemId('');
    Promise.resolve(useVaultStore.getState().refresh?.()).catch(() => {});
    connectableAccounts()
      .catch(() => [])
      .then((list) => {
        const usable = accountsForTeamRepo(list, teamRepo);
        setAccounts(usable);
        setChoice(usable[0]?.key || VAULT);
      });
  }, [isOpen, teamRepo]);

  if (!isOpen || !scope || !teamRepo) return null;
  const repoName = teamRepoLabel(teamRepo);
  const credentials = (vaultItems || []).filter((it) => GIT_CAPABLE.has(it.type));
  const options = [
    ...(accounts || []).map((a) => ({ value: a.key, label: a.label })),
    { value: VAULT, label: t('gitTeamRepo.vaultOption') },
  ];
  const useVault = choice === VAULT;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (useVault) {
        const item = credentials.find((it) => it.id === vaultItemId);
        if (!item) throw new Error(t('gitTeamRepo.chooseVaultItemError'));
        await onJoin?.(joinOptions(teamRepo, { vaultItemId: item.id, vaultItemName: item.name }));
      } else {
        await onJoin?.(joinOptions(teamRepo, (accounts || []).find((a) => a.key === choice)));
      }
    } catch (err) {
      setError(err?.message || String(err));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('gitTeamRepo.joinTitle')}
      subtitle={t('gitTeamRepo.joinSubtitle', { team: scope.name, repo: repoName })}
      customIcon={<GitBranch size={22} />}
      preventBackdropClose={loading}
    >
      {accounts === null ? (
        <p className="modal-header-subtitle" style={{ fontSize: '0.875rem' }}>{t('gitTeamRepo.lookingForAccounts')}</p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="git-join-team">
          <p className="modal-header-subtitle" style={{ fontSize: '0.875rem' }}>{t('gitTeamRepo.joinIntro')}</p>
          {accounts.length === 0 && teamRepo.provider !== 'remote' && (
            <p className="text-xs" style={{ opacity: 0.8 }}>{t('gitTeamRepo.noAccount', { host: hostOf(teamRepo.url) })}</p>
          )}

          <div>
            <span className="modal-field-label text-sm font-medium text-gray-700 dark:text-zinc-300">{t('gitTeamRepo.account')}</span>
            <div className="modal-picker" style={{ display: 'flex', width: '100%' }}>
              <CompactSelectPicker value={choice} onChange={setChoice} options={options} disabled={loading} aria-label={t('gitTeamRepo.account')} />
            </div>
          </div>

          {useVault && (
            <div>
              <span className="modal-field-label text-sm font-medium text-gray-700 dark:text-zinc-300">{t('gitTeamRepo.signInWith')}</span>
              {!vaultUnlocked ? (
                <p className="text-xs" style={{ opacity: 0.8 }}>{t('gitTeamRepo.unlockVault')}</p>
              ) : (
                <div className="modal-picker" style={{ display: 'flex', width: '100%' }}>
                  <CompactSelectPicker
                    value={vaultItemId}
                    onChange={setVaultItemId}
                    options={vaultSignInOptions(credentials)}
                    placeholder={t('gitTeamRepo.chooseVaultItem')}
                    disabled={loading}
                    aria-label={t('gitTeamRepo.signInWith')}
                  />
                </div>
              )}
            </div>
          )}

          {error && (
            <p className="text-xs" role="status" aria-live="polite" style={{ color: '#ef4444' }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center space-x-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading && <Loader className="animate-spin" size={16} />}
            <span>{loading ? t('gitTeamRepo.joining') : t('gitTeamRepo.join')}</span>
          </button>
        </form>
      )}
    </Modal>
  );
}
